import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, Plus, Star, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '../design';
import { useCreateFeeProfile, useDeleteFeeProfile, useFeeProfiles } from '../lib/feeProfiles';

const DEFAULT_DATA = {
  defaultReferralPct: 0.15,
  defaultFbaPickPack: 3.5,
  defaultStoragePerUnit: 0.3,
  defaultPrepPerUnit: 1.5,
  defaultFreightPctOfLanded: 0.08,
  defaultSupplierMarkupPct: 0.5,
  categoryOverrides: {},
  roiThresholds: { A: 0.5, B: 0.3, C: 0.15, D: 0.05, F: 0 },
};

export function FeeProfilesPage() {
  const { data: profiles, isLoading, isError, error } = useFeeProfiles();
  const [creating, setCreating] = useState(false);

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-5xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/search"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div>
              <h1 className="text-xl font-medium leading-tight">Fee profiles</h1>
              <p className="text-xs text-fg-tertiary">
                FBA cost assumptions used by the ROI calculator.
              </p>
            </div>
          </div>
          <Dialog open={creating} onOpenChange={setCreating}>
            <Button leadingIcon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              New profile
            </Button>
            <DialogContent>
              <CreateProfileForm onClose={() => setCreating(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-10 space-y-6">
        <Card elevated>
          <CardHeader>
            <CardTitle>How fee profiles work</CardTitle>
            <CardDescription>
              Each profile holds your FBA cost assumptions — referral %, pick &amp; pack, storage,
              prep, freight — plus the ROI grade thresholds that score Keepa results A through F.
            </CardDescription>
          </CardHeader>
          <ul className="text-sm text-fg-secondary space-y-1.5">
            <li>
              • One profile is the <strong className="text-fg-primary">default</strong>; new
              searches and pricing-sheet ingestions use it.
            </li>
            <li>
              • Updating a profile bumps its <code className="font-mono text-xs">version</code>;
              products scored with the old version get re-scored on next view.
            </li>
            <li>
              • Category-level overrides (e.g. higher referral % for toys) are configured in the
              profile editor.
            </li>
          </ul>
        </Card>

        {isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              Failed to load fee profiles: {(error as Error).message ?? 'Unknown error'}
            </p>
          </Card>
        )}

        {profiles && profiles.length === 0 && (
          <Card className="text-center py-10">
            <p className="text-sm text-fg-secondary mb-4">
              No fee profiles yet. Create your first one to start scoring products.
            </p>
            <Button leadingIcon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Create profile
            </Button>
          </Card>
        )}

        {profiles && profiles.length > 0 && (
          <div className="space-y-2">
            {profiles.map((p) => (
              <ProfileRow key={p.id} profile={p} canDelete={profiles.length > 1} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function ProfileRow({
  profile,
  canDelete,
}: {
  profile: {
    id: string;
    name: string;
    isDefault: boolean;
    version: number;
    data: typeof DEFAULT_DATA;
  };
  canDelete: boolean;
}) {
  const remove = useDeleteFeeProfile();
  const [feedback, setFeedback] = useState<string | null>(null);

  return (
    <Card className="flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base font-medium">{profile.name}</h3>
          {profile.isDefault && (
            <Badge variant="accent" className="gap-1">
              <Star className="size-3" /> Default
            </Badge>
          )}
          <Badge variant="neutral">v{profile.version}</Badge>
        </div>
        <p className="text-sm text-fg-secondary mt-0.5 tabular-nums">
          {(profile.data.defaultReferralPct * 100).toFixed(1)}% referral · $
          {profile.data.defaultFbaPickPack.toFixed(2)} pick/pack · $
          {profile.data.defaultPrepPerUnit.toFixed(2)} prep ·{' '}
          {(profile.data.defaultFreightPctOfLanded * 100).toFixed(1)}% freight
        </p>
        {feedback && <p className="text-xs text-negative mt-1">{feedback}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/fee-profiles/$id"
          params={{ id: profile.id }}
          className="text-sm font-medium text-accent-fg hover:underline"
        >
          Edit
        </Link>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Delete ${profile.name}`}
          loading={remove.isPending}
          disabled={!canDelete}
          onClick={async () => {
            if (!canDelete) return;
            if (!confirm(`Delete the "${profile.name}" fee profile?`)) return;
            try {
              await remove.mutateAsync(profile.id);
            } catch (err) {
              setFeedback((err as Error).message ?? 'Delete failed.');
            }
          }}
        >
          <Trash2 className="size-4 text-fg-secondary" />
        </Button>
      </div>
    </Card>
  );
}

function CreateProfileForm({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const create = useCreateFeeProfile();

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        try {
          await create.mutateAsync({
            name: name.trim(),
            data: DEFAULT_DATA,
            isDefault: makeDefault,
          });
          onClose();
        } catch {
          // surfaced via create.isError
        }
      }}
      className="space-y-5"
    >
      <DialogHeader>
        <DialogTitle>New fee profile</DialogTitle>
        <DialogDescription>
          Starts with sensible defaults — open the editor afterwards to tune fees, freight, and ROI
          thresholds.
        </DialogDescription>
      </DialogHeader>

      <FormField label="Name" htmlFor="name" required>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Toys, Default, Aggressive"
        />
      </FormField>

      <label className="flex items-center gap-2 text-sm text-fg-primary cursor-pointer">
        <input
          type="checkbox"
          checked={makeDefault}
          onChange={(e) => setMakeDefault(e.target.checked)}
          className="size-4 rounded-xs border-border-default accent-[rgb(var(--accent-primary))]"
        />
        Make this the default profile
      </label>

      {create.isError && (
        <div className="rounded-sm bg-negative/[0.12] border border-negative/30 px-3 py-2">
          <p className="text-sm text-negative">{(create.error as Error).message}</p>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
          Create
        </Button>
      </DialogFooter>
    </form>
  );
}
